import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PeopleIcon from '@mui/icons-material/People';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import DataGrid from 'app/components/DataGrid';
import classes from 'app/components/DataGrid/classes.module.scss';
import useCatalogs from 'app/hooks/useCatalogs';
import CatalogModal from './CatalogModal';
import MomentsCell from 'app/components/DataGrid/MomentsCell';
import CustomersCell from 'app/components/DataGrid/CustomersCell';
import useDeeplink from 'app/hooks/useDeeplink';
import JobUploadProgressBar from 'app/components/Jobs/JobUploadProgressBar';

import {
  getCatalog,
  getCatalogs as exportCatalogsSvc
} from 'app/services/catalogs';

import {
  getKiosksV2
} from 'app/services/kiosks';

import {
  getJobConfigs
} from 'app/services/jobConfigs';

import {
  setMomentParams
} from 'app/actions/moments';

import {
  setPhotoSwipeOpen,
  setPhotoSwipeMoment
} from 'app/actions/photoSwipeActions';

import {
  AND_OPERATOR,
} from 'app/hooks/useFilter';

import {
  formatKioskCellValue,
  formatJobConfigCellValue
} from 'app/utils/valueFormatters';

import {
  formatOptions
} from 'app/utils/options';

import {
  moments
} from 'app/resources';

import {
  getLocalizedDateTime
} from 'shared/utils/Utils';

const Catalogs = props => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.userReducer.user);
  const deeplink = useDeeplink();

  const account = useSelector(state => state.portalReducer.account);
  const showProduct = account.feature_flags.catalog_products;

  // load needed resources on first render
  const [kiosks, setKiosks] = useState([]);
  const [jobConfigs, setJobConfigs] = useState([]);

  useEffect(() => {
    getKiosksV2({ page: 1, per_page: 100 })
      .then(resp => setKiosks(resp.records));

    getJobConfigs({ page: 1, per_page: 100 })
      .then(resp => setJobConfigs(resp.records));
  }, []);

  const {
    resource,
    formIsOpen,
    currentRecord,
    onFormClose,
    onAdd,
    onEdit,
    onQuickAdd,
    onQuickEdit,
    onDataGridLoad,
    onFormSubmit,
    onDestroy,
    showFilters,
    getRowData,
    updateRow,
    getFilterModel
  } = useCatalogs();

  const exportCSV = () => {
    let params = {
      page: 0,
      per_page: 1000,
      is_export: true
    }

    const filterModel = getFilterModel();
    if (filterModel) {
      params.filter_model = filterModel;
    }

    exportJobsSvc(params).then((resp => resp.blob())).then((blob) => {
      const elem = window.document.createElement('a');
      elem.href = window.URL.createObjectURL(blob);
      elem.download = `jobs_${new Date().toISOString()}.csv`;
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);
    })
  }

  const viewDefaultMomentsForJob = async jobId => {
    // filter moments by job ahead of showing the moments table.
    // this will overwrite any filters previously set.
    // currently the filter expects values to be strings
    let filterModel = {
      job_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: jobId.toString()
      }],
      is_default: [{
        filterClass: 'text',
        filterType: 'equals',
        value: '1'
      }]
    }

    deeplink.load(moments, filterModel);
  }

  const header = 'Catalogs';
  const addButtonLabel = 'Add Catalog';

  const columnOrder = [
    'default_moments',
    'upload_status',
    'name',
    'kiosk',
    'job_config',
    'purchase_count',
    'percent_purchased',
    'purchase_total'
  ];

  const onlyColumns = [
    'id',
    'created_at',
    'updated_at',
    'event_id',
    'name',
    'kiosk_id',
    'job_config',
    'account_id',
    'provider_account_id',
    'lab_name',
    'provider_name',
    'purchase_count',
    'percent_purchased',
    'purchase_total',
    'default_moments',
    'upload_status',
    'kiosk'
  ];

  let visibleColumns = [
    'upload_status',
    'name',
    'default_moments',
    'purchase_count',
    'percent_purchased',
    'purchase_total',
    'kiosk'
  ]

  if (showProduct) {
    visibleColumns.push('job_config');
  }

  const kioskOptions = formatOptions(kiosks);

  // filter out Digital Delivery (catalogs have None option instead)
  const jobConfigOptions = formatOptions(jobConfigs.filter(jc => jc.id !== -1));

  const onMomentClick = moment => {
    dispatch(setPhotoSwipeMoment(moment));
    dispatch(setPhotoSwipeOpen(true));
  }

  const setPhotoSwipeItem = job => {
    if (job.cover_moment) {
      return {
        id: job.cover_moment.id,
        src: job.cover_moment.preview_url,
        w: job.cover_moment.width,
        h: job.cover_moment.height,
        digital_edit: job.cover_moment.digital_edit,
        orig_moment_id: job.cover_moment.orig_moment_id,
      }
    }
  };

  // setup callbacks for uploads via both modal and grid cell
  const onUploadStart = (upload, { job }) => {
    job.last_upload = upload;
    updateRow(job);
  }

  const onUploadComplete = async (upload, { job }) => {
    let updatedJob = await getCatalog(job.id);
    updatedJob.last_upload = upload;
    updateRow(updatedJob);
  }

  const columnDefOverrides = [
    {
      field: 'default_moments',
      headerName: 'Catalog Moments',
      pinned: 'left',
      inputType: 'number',
      width: 11 * 16,
      thumbnail: data => data?.default_cover_moment?.thumb_url,
      onThumbnailClick: data => onMomentClick(data.cover_moment),
      cellRenderer: params => {
	const {
          data,
	  colDef
	} = params;

        if (data) {
          const {
	    id,
	    actual_photo_count,
	    template_story_id
	  } = data;

          const onImagesClick = () => viewDefaultMomentsForJob(id);

	  // disabling the upload label for now,
	  // as uploads from here are disabled
	  let showUploadLabel = false;
	  // only show UPLOAD label if no images
	  //let showUploadLabel = !total_photo_count;

	  return (
	    <MomentsCell
	      data={data}
	      colDef={colDef}
	      showImageCount={true}
	      imageCount={actual_photo_count}
	      onImagesClick={onImagesClick}
	      showUpload={false}
	      showUploadLabel={showUploadLabel}
	      uploadJobId={id}
	      uploadStoryIds={[template_story_id]}
	      uploadEntityId={id}
	      onUploadStart={onUploadStart}
	      onUploadComplete={onUploadComplete}
	      job={data}
	    />
	  );
        }
        return '';
      }
    },
    {
      field: 'upload_status',
      headerName: 'Last Upload / Status',
      //pinned: 'left',
      width: 220,
      cellRenderer: params => {
	let { data } = params;
        let upload = data?.last_upload;
        if (upload) {
          if (upload.completed_at) {
            let lastUpload = getLocalizedDateTime(user.locale, upload.completed_at);
            return <span className={classes.gridCell}>{lastUpload}</span>;
          } else {
            return <JobUploadProgressBar job={data} />;
          }
        }
        return '';
      }
    },
    {
      field: 'name',
      minWidth: 20 * 16, // 20rem
      quickEdit: true,
      renderFields: ['name', 'event_id']
    },
    { field: 'event_id', headerName: 'Job ID' },
    { field: 'created_at', formatType: 'date', inputType: 'date' },
    { field: 'updated_at', formatType: 'date', inputType: 'date' },
    { field: 'percent_purchased', headerName: '% Purchased', sortable: false, filterDisabled: true },
    { field: 'purchase_count', inputType: 'number' },
    { field: 'purchase_total', sortable: false, filterDisabled: true },
    { field: 'id', minWidth: 10 * 16 },
    {
      field: 'kiosk',
      headerName: 'Kiosk',
      valueFormatter: value => formatKioskCellValue(value),
      inputType: 'select',
      inputOptions: kioskOptions,
      quickEdit: true
    },
    {
      field: 'job_config',
      headerName: 'Product',
      valueFormatter: value => formatJobConfigCellValue(value),
      inputType: 'select',
      inputOptions: jobConfigOptions,
      quickEdit: true
    },
  ]

  const actions = [
    { key: 'destroy', label: 'Delete Catalogs', callback: onDestroy }
  ]

  const buttons = [
    {
      key: 'export',
      icon: <FileDownloadIcon />,
      label: 'CSV',
      onClick: exportCSV
    }
  ]

  return (
    <div>
      <DataGrid
        resource={resource}
        header={header}
        getRowData={getRowData}
        addButtonLabel={addButtonLabel}
        onAdd={onAdd}
        showEdit={true}
        onEdit={onEdit}
        showQuickEdit={true}
        onQuickAdd={onQuickAdd}
        onQuickEdit={onQuickEdit}
        columnOrder={columnOrder}
        columnDefOverrides={columnDefOverrides}
        setPhotoSwipeItem={setPhotoSwipeItem}
        onlyColumns={onlyColumns}
        visibleColumns={visibleColumns}
        showFilters={showFilters}
        showSelect={true}
        actions={actions}
        loadingMessage="Loading catalogs..."
        onVisibleColumnsUpdate={columns => { console.log(columns) }}
        onDataGridLoad={onDataGridLoad}
        buttons={buttons}
      />
      <CatalogModal
        isOpen={formIsOpen}
        onClose={onFormClose}
        job={currentRecord}
        kiosks={kiosks}
        onFormSubmit={onFormSubmit}
        onDestroy={onDestroy}
	onUploadStart={onUploadStart}
	onUploadComplete={onUploadComplete}
	user={user}
	updateRow={updateRow}
        jobConfigs={jobConfigs}
      />
    </div>
  );
}

export default Catalogs;
